<template>
  <div class="szw-col szw-overflow-hidden szw-relative szw-w-full" ref="scrollingContainerRef" :style="{ height }">
    <slot />
  </div>

  <bottom-tabs
    v-if="overrideBottomNavigationVisibility || isBottomNavigationVisible"
    @nav-up="navigationUpHandler"
    @nav-down="navigationDownHandler"
    :is-up-disabled-tab="isUpDisabledTab || disableBottomNavigation || isUpNavigationDisabled"
    :is-down-disabled-tab="isDownDisabledTab || disableBottomNavigation || isDownNavigationDisabled"
    :full-width-buttons="fullWidthButtons"
    :offset="buttonOffset"
  />
</template>

<script setup>
import { ref, computed, nextTick, watch, inject, onBeforeUnmount, onMounted } from 'vue'
import BottomTabs from '@/__core__/components/navigation/bottom-tabs/BottomTabs.vue'
import useSize from '@/__core__/composables/useSize'

const BOTTOM_TABS_WITH_OFFSET_HEIGHT = 40 + 8 + 8 // 40px tab height + 8px bottom padding + 8px gap between container and tabs

/** @type {{ containerRef: Ref<HTMLDivElement> }} */
const props = defineProps({
  widgetContainerHeight: {
    type: Number,
    default: 600
  },
  containerRef: {
    type: Object,
    default: () => null
  },
  containerId: {
    type: String,
    default: () => 'default'
  },
  overrideScrollOffset: {
    type: Number,
    default: 0
  },
  disableBottomNavigation: {
    type: Boolean,
    default: false
  },
  disableAnimationWhenReseting: {
    type: Boolean,
    default: false
  },
  overrideBottomNavigationVisibility: {
    type: Boolean,
    default: false
  },
  fullWidthButtons: {
    type: Boolean,
    default: false
  },
  buttonOffset: {
    type: Number,
    default: 16
  },
  isDownNavigationDisabled: {
    type: Boolean,
    default: false
  },
  isUpNavigationDisabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['recalculated', 'scroll-index-changed', 'scroll-reset'])

const status = inject('status')

/** @type {Ref<HTMLDivElement>} */
const scrollingContainerRef = ref(null)
const translateY = ref(0)
const scrollOffset = ref(0)
const offsetTop = ref(0)
const scrollIndex = ref(0)

const isUpDisabledTab = ref(true)
const isDownDisabledTab = ref(false)
const isBottomNavigationVisible = ref(true)

const bottomTabsWithOffsetHeight = computed(() => (isBottomNavigationVisible.value ? BOTTOM_TABS_WITH_OFFSET_HEIGHT : 0))

const height = computed(() => `calc(${props.widgetContainerHeight}px - ${offsetTop.value}px - ${bottomTabsWithOffsetHeight.value}px)`)

const navigationUpHandler = () => {
  // if the container will be fully visible after the scroll, hide the top navigation
  if (translateY.value + scrollOffset.value >= 0) {
    translateY.value = 0
    scrollIndex.value = 0
    scroll()

    isUpDisabledTab.value = true
  } else {
    translateY.value += scrollOffset.value
    scrollIndex.value -= 1
    scroll()

    isUpDisabledTab.value = false
  }
}

const navigationDownHandler = () => {
  // find out how much the container is scrolled + already visible part
  const scrolledWithVisible = scrollingContainerRef.value.clientHeight + Math.abs(translateY.value)
  const containerHeight = props.containerRef.clientHeight

  if (scrolledWithVisible + scrollOffset.value >= containerHeight) {
    translateY.value = scrollingContainerRef.value.clientHeight - containerHeight
    scrollIndex.value++
    scroll()

    isDownDisabledTab.value = true
  } else {
    translateY.value -= scrollOffset.value
    scrollIndex.value++
    scroll()

    isDownDisabledTab.value = false
  }
}

const recalculate = () => {
  if (!props.containerRef) {
    return
  }

  // obtain height and number of children of the container which is scrollable
  const containerHeight = props.containerRef.clientHeight
  const childrenCount = props.containerRef.childElementCount

  offsetTop.value = scrollingContainerRef?.value?.offsetTop

  // if container is fully visible in the viewport, hide the bottom navigation
  if (containerHeight <= scrollingContainerRef.value.clientHeight + 4 + bottomTabsWithOffsetHeight.value) {
    isBottomNavigationVisible.value = false
    return
  } else {
    isBottomNavigationVisible.value = true
  }

  const signleChildHeight = Math.round(containerHeight / childrenCount)
  let desiredScrollOffset = Math.floor(scrollingContainerRef.value.clientHeight / (2 * signleChildHeight)) * signleChildHeight

  // KNOCKOUT PHASE WIDGET LOGIC
  if (signleChildHeight > scrollingContainerRef.value.clientHeight) {
    desiredScrollOffset = props.widgetContainerHeight - offsetTop.value - bottomTabsWithOffsetHeight.value
  }

  scrollOffset.value = !props.overrideScrollOffset ? desiredScrollOffset : props.overrideScrollOffset
  offsetTop.value = scrollingContainerRef?.value?.offsetTop

  emit('recalculated')
}

const scroll = () => props.containerRef.style.setProperty('transform', `translateY(${translateY.value}px)`)

useSize(scrollingContainerRef, 0, recalculate)

const unwatchTabStatus = watch(
  () => translateY.value,
  newValue => {
    if (newValue === 0) {
      isUpDisabledTab.value = true
    } else {
      isUpDisabledTab.value = false
    }

    const scrolledWithVisible = scrollingContainerRef.value.clientHeight + Math.abs(newValue)

    if (scrolledWithVisible >= props.containerRef.clientHeight) {
      isDownDisabledTab.value = true
    } else {
      isDownDisabledTab.value = false
    }
  }
)

const unwatch = watch(
  () => status.value,
  async newStatus => {
    if (newStatus !== 'success') {
      return
    }

    await nextTick(recalculate)
  }
)

const unwatchScrollIndex = watch(
  () => scrollIndex.value,
  newScrollIndex => {
    emit('scroll-index-changed', newScrollIndex)
  }
)

const unwatchContainerRef = watch(
  () => props.containerRef,
  async () => {
    await nextTick(recalculate)
  }
)

const unwatchContainerId = watch(
  () => props.containerId,
  async () => {
    translateY.value = 0
    scrollIndex.value = 0

    emit('scroll-reset')

    scroll()
    await nextTick(recalculate)
  }
)

const unwatchWidgetContainerHeight = watch(
  () => props.widgetContainerHeight,
  async () => {
    await nextTick(recalculate)
  }
)

onMounted(async () => {
  scrollOffset.value = props.overrideScrollOffset
  offsetTop.value = scrollingContainerRef?.value?.offsetTop
})

onBeforeUnmount(() => {
  unwatch()
  unwatchTabStatus()
  unwatchContainerId()
  unwatchContainerRef()
  unwatchWidgetContainerHeight()
  unwatchScrollIndex()
})
</script>
